import React from 'react';
import { Button, Chip, Dialog, DialogContent, Grid, IconButton, Typography } from '@mui/material';
import { BusinessHour2, BusinessHourValue, FirstMenu, PostBusinessHour2Params } from '../../models';
import _ from 'lodash';
import { firstMissingPositive, generateBusinessHour2, getDate, toggle } from '../../constants/utils';
import { ReactComponent as MingcuteCloseFillIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as MenuBookOutlineIcon } from '../../static/images/svg/material-symbols_menu-book-outline.svg';
import { ReactComponent as TablerChevronDownIcon } from '../../static/images/svg/tabler_chevron-down.svg';
import { ReactComponent as IcRoundPlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import { ReactComponent as BusinessHourCopyStackIcon } from '../../static/images/svg/business-hour_copy-stack.svg';
import moment from 'moment';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import ButtonComponent from '../../component/button/button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import './scss/business-hour-compound.scss';

/** 周一至周日固定行（date 为 0–6）；节假日为同 type=0 但 date=7，须允许删最后时段 */
function isCompoundWeekdayMonToSunRow(item: BusinessHour2): boolean {
    return item.type === '0' && ['0', '1', '2', '3', '4', '5', '6'].includes(String(item.date ?? ''));
}

/** 是否已有节假日行（type=0 且 date=7） */
function hasCompoundHolidayRow(businessHour2: BusinessHour2[]): boolean {
    return businessHour2.some((row) => row.type === '0' && String(row.date ?? '') === '7');
}

export default class BusinessHourCompound extends React.Component<Props, State> {
    public state: Readonly<State> = {
        // 当前展开的项
        expandId: '',
        // 表单校验
        errorId: '',
        // 提示框
        isShowConfirmDialog: false,
        /** 复制营业时间时的源行下标（对应该周期） */
        copySourceIndex: null as number | null,
        /** 新增日期行后滚动定位到该行（对应 item.id） */
        pendingScrollToDayId: null as string | null,
        /** 「新增菜单」弹窗：选中的时间段 key + 字段 + 展示用文案 */
        menuModal: null,
    }
    // *********************
    // Life Cycle Function
    // *********************
    constructor(props: Props) {
        super(props);
        this.addBusinessHour2 = this.addBusinessHour2.bind(this);
        this.addHolidayBusinessHour2 = this.addHolidayBusinessHour2.bind(this);
        this.handleDateChange = this.handleDateChange.bind(this);
        this.onSave = this.onSave.bind(this);
    }
    // *********************
    // Default Function
    // *********************

    // 新增一条营业时间数据
    addBusinessHour2() {
        const { businessHour2, defaultMaxRound, defaultTurnMins, defaultTurnDishes } = this.props;
        const date = moment().format('YYYY-MM-DD');
        const newData = generateBusinessHour2(date, '1', defaultTurnDishes, defaultTurnMins, defaultMaxRound);
        businessHour2.push(newData);
        this.props.handleChange(businessHour2);
        if (newData.id) {
            this.setState({ pendingScrollToDayId: newData.id });
        }
    }

    /** 新增节假日行（date=7, type=0）；插在首条自定义日期之前，否则末尾 */
    addHolidayBusinessHour2() {
        const { businessHour2, defaultMaxRound, defaultTurnMins, defaultTurnDishes } = this.props;
        if (hasCompoundHolidayRow(businessHour2)) {
            return;
        }
        const newData = generateBusinessHour2('7', '0', defaultTurnDishes, defaultTurnMins, defaultMaxRound);
        const insertAt = businessHour2.findIndex((row) => row.type === '1');
        if (insertAt >= 0) {
            businessHour2.splice(insertAt, 0, newData);
        } else {
            businessHour2.push(newData);
        }
        this.props.handleChange(businessHour2);
        if (newData.id) {
            this.setState({ pendingScrollToDayId: newData.id });
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { pendingScrollToDayId } = this.state;
        if (
            pendingScrollToDayId == null ||
            pendingScrollToDayId === prevState.pendingScrollToDayId
        ) {
            return;
        }
        const id = pendingScrollToDayId;
        const tryScroll = () => {
            const el = document.getElementById(`business-hour-compound-day-${id}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                (el as HTMLElement).focus({ preventScroll: true });
                this.setState({ pendingScrollToDayId: null });
                return true;
            }
            return false;
        };
        requestAnimationFrame(() => {
            if (!tryScroll()) {
                requestAnimationFrame(() => {
                    if (!tryScroll()) {
                        this.setState({ pendingScrollToDayId: null });
                    }
                });
            }
        });
    }

    // 新增一条营业时间时间段
    addBusinessHourValue(index: number) {
        const { businessHour2, defaultTurnDishes, defaultTurnMins, defaultMaxRound } = this.props;
        const curValue: BusinessHourValue[] = _.get(businessHour2, `${index}.value2`, []) || [];
        const ids = curValue.map((item) => item.id || 0)
        const id = firstMissingPositive(ids)
        curValue.push(
            {
                id,
                data_type: '1',
                default_menu: [],
                hide_menu: [],
                preferred_menu: [],
                alternative_menu: [],
                end_time: '24:00',
                start_time: '00:00',
                value: '1',
                project: [],
                selftake_status: '0',
                takeaway_status: '0',
                turn_dishes: defaultTurnDishes,
                turn_mins: defaultTurnMins,
                max_turn: defaultMaxRound,
            }
        )
        _.set(businessHour2, `${index}.value2`, curValue)
        this.props.handleChange(businessHour2)
    }

    /**
     * 删除营业时间时间段
     * @param index
     * @param valueIndex
     */
    deleteBusinessHourValue(index: number, valueIndex: number) {
        const { businessHour2 } = this.props;
        const curBusinessHour = _.get(businessHour2, index);
        const key = curBusinessHour.pattern === '0' ? 'value' : 'value2';
        const curValue: BusinessHourValue[] = _.get(businessHour2, `${index}.${key}`, []);
        curValue.splice(valueIndex, 1);
        if (curValue.length === 0) {
            businessHour2.splice(index, 1)
        }
        this.props.handleChange(businessHour2)
        this.setState({
            errorId: ''
        })
    }

    /**
     * 修改数据
     * @param value
     * @param key
     */
    onChange(value: string | string[], key: string) {
        const { businessHour2 } = this.props;
        _.set(businessHour2, key, value);
        this.props.handleChange(businessHour2);
    }

    /**
     * 时间
     * @param value
     * @param key
     */
    handleDateChange(value: {
        startTime: Date | null;
        endTime: Date | null
    }, key: string) {
        _.forEach(value, (v, i) => {
            let newValue = moment(v).format(i === 'startTime' ? 'HH:mm' : 'kk:mm');
            // 处理00:10的情况
            if (moment(v).hour() === 0 && moment(v).minute() !== 0) {
                newValue = moment(v).format('HH:mm');
            }
            const newKey = i === 'startTime' ? `${key}.start_time` : `${key}.end_time`;
            this.onChange(newValue, newKey)
        })
        this.setState({
            errorId: ''
        })
    }

    /**
     * 表单回调
     * @param value
     * @param key
     * @param itemKey
     */
    onFormChange(value: string, key: string, itemKey: string) {
        const { businessHour2 } = this.props;
        const oldValue: string[] = _.get(businessHour2, `${key}.${itemKey}`, []) || []
        if (['turn_mins', 'turn_dishes', 'max_turn'].includes(itemKey)) {
            this.onChange(value, `${key}.${itemKey}`)
        }
        // 首选菜单是单选
        else if (itemKey === 'preferred_menu') {
            this.onChange(_.get(oldValue, 0) === value ? [] : [value], `${key}.${itemKey}`)
        }
        // 其他菜单是多选
        else {
            const newValue = toggle(oldValue, value)
            this.onChange(newValue, `${key}.${itemKey}`)
        }
    }

    // 保存营业时间
    onSave() {
        const { businessHour2 } = this.props;
        // $ 表单校验
        const date = businessHour2.filter((item) => item.type === '1').map((item) => item.date || '');
        // $ 日期重复
        if (date.length !== _.xor(date).length) {
            GlobalNotification.error(i18n.t('business_hour_page_date_prevent_adding_repeat'));
            return;
        }
        // $ 时间重叠
        let error = false;
        // 对营业时间排序
        for (let j = 0, jLength = businessHour2.length; j < jLength; j++) {
            const value = businessHour2[j].pattern === '0' ? businessHour2[j]!.value : businessHour2[j]!.value2
            if (value) {
                value!.map(item => {
                    if (error) return;
                    const name = businessHour2[j].type === '0'
                        ?
                        getDate(Number(businessHour2[j].date))
                        :
                        businessHour2[j].date;
                    if (item.preferred_menu!.length === 0) {
                        error = true;
                        GlobalNotification.error(`${name} ${i18n.t('business_hour_page_had_not_select_preferred_menu')}`);
                        return
                    }
                    if (!item.turn_mins || item.turn_mins < 1 || item.turn_mins > 9999) {
                        error = true;
                        GlobalNotification.error(`${name} ${i18n.t('self_order_setting_page_one_sending_per_round_time_min')}： ${i18n.t('input_rule_text_must_be_an_integer_greater_than_1_and_less_than_9999')}`);
                    }
                    if (!item.turn_dishes || item.turn_dishes < 1 || item.turn_dishes > 9999) {
                        error = true;
                        GlobalNotification.error(`${name} ${i18n.t('self_order_setting_page_each_person_per_round_oder_count')}： ${i18n.t('input_rule_text_must_be_an_integer_greater_than_1_and_less_than_9999')}`);
                    }
                    if (!item.max_turn || item.max_turn < 1 || item.max_turn > 9999) {
                        error = true;
                        GlobalNotification.error(`${name} ${i18n.t('self_order_setting_page_rule_global_turn_max_number')}： ${i18n.t('input_rule_text_must_be_an_integer_greater_than_1_and_less_than_9999')}`);
                    }
                })
            }
            if ((businessHour2[j].value2 || []).length > 1) {
                // 排序
                const value2 = _.sortBy(businessHour2[j].value2, 'start_time');
                const InvalidDate = value2.find(item => item.start_time === 'Invalid date' || item.end_time === 'Invalid date');
                if (InvalidDate) {
                    error = true;
                    GlobalNotification.error(`Invalid date`);
                    break;
                }
                // 判断是否有重叠
                for (let i = 1, iLength = value2.length; i < iLength; i++) {
                    if ((value2[i - 1].end_time || '00:00') > (value2[i].start_time || '00:00')) {
                        error = true;
                        const name = businessHour2[j].type === '0'
                            ?
                            getDate(Number(businessHour2[j].date))
                            :
                            businessHour2[j].date;
                        GlobalNotification.error(`${name} ${i18n.t('business_hour_page_time_phase_overlap')}`);
                        this.setState({
                            errorId: businessHour2[j].id || ''
                        })
                        // 错误提示
                        document.getElementById(`${businessHour2[j].id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        break;
                    }
                }

            }
            if (error) {
                break
            }
        }
        if (error) {
            return;
        }
        const result = businessHour2.reduce((prev: PostBusinessHour2Params[], cur) => {
            // 24小时营业
            const value = (cur.value || []).map((item) => (this.transform(cur, item)));
            // 自主模式
            const value2 = (cur.value2 || []).map((item) => (this.transform(cur, item)));
            return _.concat(prev, value, value2)
        }, [])
        this.props.onSave(result)
    }

    /**
     * 格式转换
     * @param businessHour
     * @param businessHourValue
     * @returns
     */
    transform(businessHour: BusinessHour2, businessHourValue: BusinessHourValue): PostBusinessHour2Params {
        const result: PostBusinessHour2Params = {
            //  营业时间的描述（0：营业；1：不营业；）
            value: businessHourValue.value || '0',
            // 数据模式（0?:24小时；1：自主模式）
            data_type: businessHourValue.data_type || '0',
            // 前端模式设置（0?:24小时；1：自主模式）
            pattern: businessHour.pattern || '0',
            // 营业时间的类型（0：星期 + 时间；1：日期 + 时间；）
            type: businessHour.type || '0',
            // 2022-03-24 日期必传 星期不用传
            date: businessHour.date,
            // '0-6' 0周日 6周六 星期必传 日期不用传
            week: businessHour.date,
            // 首选菜单
            preferred_menu: businessHourValue.preferred_menu || [],
            // 备选菜单
            alternative_menu: businessHourValue.alternative_menu || [],
            // 必选菜单
            default_menu: businessHourValue.default_menu || [],
            // 隐藏菜单
            hide_menu: businessHourValue.hide_menu || [],
            // 结束时间 "01:10"
            end_time: businessHourValue.data_type === '0' ? '24:00' : (businessHourValue.end_time || '00:00'),
            // 开始时间
            start_time: businessHourValue.data_type === '0' ? '00:00' : (businessHourValue.start_time || '00:00'),
            // 项目 "QOP"; "COP2"; "RWA2"; "POS"
            project: businessHourValue.project || [],
            // 暂时用不到 默认0
            // 是否允许自提 (0：自提；1：非自提)
            selftake_status: '0',
            // 是否允许外卖（0：外卖；1：非外卖）
            takeaway_status: '0',
            // 每轮点餐数量
            turn_dishes: Number(businessHourValue.turn_dishes || 0),
            // 每轮点餐时间
            turn_mins: Number(businessHourValue.turn_mins || 0),
            // 最大轮次
            max_turn: Number(businessHourValue.max_turn || 0),
        }
        return result
    }

    /**
     * 判断按钮是否禁用
     * @param key
     * @param value
     */
    getBtnDisable(key: string, data: BusinessHourValue, value: string) {
        let disabled = false;
        if (key !== 'project') {
            const arr = ['alternative_menu', 'default_menu', 'hide_menu', 'preferred_menu'].filter((item) => item !== key);
            for (let i = 0; i < 3; i++) {
                const menu: string[] = _.get(data, arr[i], []) || [];
                if (menu.indexOf(value) > -1) {
                    disabled = true;
                    break
                }
            }
        }
        return disabled
    }

    /**
     * 将「当前行」的营业时间复制到其余所有周期（pattern / value / value2）
     */
    applyCopyFromRow = () => {
        const { copySourceIndex } = this.state;
        if (copySourceIndex == null) {
            this.setState({ isShowConfirmDialog: false });
            return;
        }
        const newHour2 = _.cloneDeep(this.props.businessHour2);
        const source = newHour2[copySourceIndex];
        if (!source) {
            this.setState({ isShowConfirmDialog: false, copySourceIndex: null });
            return;
        }
        newHour2.forEach((item, i) => {
            if (i !== copySourceIndex) {
                item.pattern = _.cloneDeep(source.pattern);
                item.value = _.cloneDeep(source.value);
                item.value2 = _.cloneDeep(source.value2);
            }
        });
        this.props.handleChange(newHour2);
        this.setState({
            isShowConfirmDialog: false,
            copySourceIndex: null,
        });
    };

    /** 时间段展示文案（设计稿「周一 · 09:00 - 12:00」） */
    formatSlotContextLabel(item: BusinessHour2, valueItem: BusinessHourValue) {
        const day = item.type === '0' ? getDate(Number(item.date)) : (item.date || '');
        const start = valueItem.start_time || '00:00';
        const end = valueItem.end_time || '24:00';
        const fmt = (t: string) => {
            const m = moment(t, ['HH:mm', 'H:mm', 'kk:mm'], true);
            return m.isValid() ? m.format('HH:mm') : t;
        };
        return `${day} · ${fmt(start)} - ${fmt(end)}`;
    }

    closeMenuModal = () => this.setState({ menuModal: null });

    openMenuModal = (
        index: number,
        patternKey: 'value' | 'value2',
        valueIndex: number,
        fieldKey: string,
        item: BusinessHour2,
        valueItem: BusinessHourValue,
    ) => {
        this.setState({
            menuModal: {
                index,
                patternKey,
                valueIndex,
                fieldKey,
                dayTimeLabel: this.formatSlotContextLabel(item, valueItem),
            },
        });
    };

    /** 菜单名称展示 */
    menuLabel(menuOptions: { value: string; label: string }[], id: string) {
        return menuOptions.find((o) => o.value === id)?.label || id;
    }

    /** 展开/收起「菜单设置」：与菜单书图标同一可点击区域 */
    toggleSlotExpand = (valueItem: BusinessHourValue, item: BusinessHour2) => {
        this.setState((prev) => ({
            expandId:
                prev.expandId === `${valueItem.id!}#${item.id}` ? '' : `${valueItem.id!}#${item.id}`,
        }));
    };

    renderSlotExpandToggle(item: BusinessHour2, valueItem: BusinessHourValue, isExpand: boolean) {
        return (
            <IconButton
                size="small"
                className="business-hour-compound-slot__expand-toggle"
                onClick={() => this.toggleSlotExpand(valueItem, item)}
                aria-expanded={isExpand}
                aria-label={i18n.t('business_hour_compound_menu_settings')}
            >
                <MenuBookOutlineIcon className="business-hour-compound-slot__menu-book-icon" aria-hidden />
                <TablerChevronDownIcon
                    className={`business-hour-compound-slot__chevron${isExpand ? ' business-hour-compound-slot__chevron--expanded' : ''}`}
                    aria-hidden
                />
            </IconButton>
        );
    }

    // *********************
    // View
    // *********************

    render() {
        const { expandId, errorId, isShowConfirmDialog } = this.state;
        const { businessHour2, menuList, sectionTitle, complexModeToggle } = this.props;
        const showAddHolidayButton = !hasCompoundHolidayRow(businessHour2);
        const menuOptions = menuList.map((item) => ({
            value: `${item.main_group_id}` || '',
            label: item.main_group_name || ''
        }))
        const inputFormArr = [
            {
                label: i18n.t('self_order_setting_page_one_sending_per_round_time_min_default'),
                key: 'turn_mins',
                type: 'input',
                inputType: 'number',
                regularType: 'roundRules'
            },
            {
                label: i18n.t('self_order_setting_page_each_person_per_round_oder_count_default'),
                key: 'turn_dishes',
                type: 'input',
                inputType: 'number',
                regularType: 'roundRules'
            },
            {
                label: i18n.t('self_order_setting_page_rule_global_turn_max_number_default'),
                key: 'max_turn',
                type: 'input',
                inputType: 'number',
                regularType: 'roundRules'
            },
        ]
        const formArr = [
            {
                key: 'project',
                label: i18n.t('expand_page_project'),
                options: [
                    {
                        value: 'QOP',
                        label: 'QOP'
                    },
                    {
                        value: 'COP2',
                        label: 'COP2'
                    },
                    {
                        value: 'COP3',
                        label: 'COP3'
                    },
                    {
                        value: 'RWA2',
                        label: 'RWA2'
                    },
                    {
                        value: 'POS',
                        label: 'POS'
                    },
                ],
                inputType: 'SELECT_MUL' as const,
            },
            {
                key: 'preferred_menu',
                label: i18n.t('business_hour_page_preferred_menu'),
                options: menuOptions,
                inputType: 'SELECT_MUL' as const,
            },
            {
                key: 'alternative_menu',
                label: i18n.t('business_hour_page_alternative_menu'),
                options: menuOptions,
                inputType: 'SELECT_MUL' as const,
            },
            {
                key: 'default_menu',
                label: i18n.t('business_hour_page_required_menu'),
                options: menuOptions,
                inputType: 'SELECT_MUL' as const,
            },
            {
                key: 'hide_menu',
                label: i18n.t('business_hour_page_hide_menu'),
                options: menuOptions,
                inputType: 'SELECT_MUL' as const,
            },
        ]
        const { menuModal } = this.state;

        const renderMenuSection = (
            formItem: typeof formArr[number],
            key: string,
            valueItem: BusinessHourValue,
            item: BusinessHour2,
            dayIndex: number,
            slotValueIndex: number,
        ) => {
            const selected: string[] = (_.get(valueItem, formItem.key, []) as string[]) || [];
            const patternKey = item.pattern === '0' ? 'value' : 'value2';
            const hint =
                formItem.key === 'preferred_menu'
                    ? i18n.t('business_hour_compound_preferred_menu_hint')
                    : formItem.key === 'hide_menu'
                        ? i18n.t('business_hour_compound_hide_menu_hint')
                        : '';
            return (
                <div className="business-hour-compound-expand__section" key={formItem.key}>
                    <Typography className="business-hour-compound-expand__section-title">{formItem.label}</Typography>
                    {hint ? (
                        <Typography className="business-hour-compound-expand__section-hint">{hint}</Typography>
                    ) : null}
                    <div className="business-hour-compound-expand__chips">
                        {selected.map((mid) => (
                            <Chip
                                key={`${formItem.key}-${mid}`}
                                label={this.menuLabel(menuOptions, mid)}
                                onDelete={() => this.onFormChange(mid, key, formItem.key)}
                                deleteIcon={
                                    <MingcuteCloseFillIcon
                                        className="business-hour-compound-menu-chip__close"
                                        aria-hidden
                                        focusable="false"
                                    />
                                }
                                className="business-hour-compound-menu-chip"
                                variant="outlined"
                            />
                        ))}
                        <Button
                            variant="outlined"
                            className="business-hour-compound-add-menu-btn"
                            endIcon={
                                <IcRoundPlusIcon
                                    className="business-hour-compound-add-menu-btn__plus"
                                    aria-hidden
                                    focusable="false"
                                />
                            }
                            onClick={() =>
                                this.openMenuModal(dayIndex, patternKey, slotValueIndex, formItem.key, item, valueItem)
                            }
                        >
                            {i18n.t('business_hour_compound_add_menu_plus')}
                        </Button>
                    </div>
                </div>
            );
        };

        const renderExpandPanel = (dayIndex: number, item: BusinessHour2, valueItem: BusinessHourValue, valueIndex: number) => {
            const key = `${dayIndex}.${item.pattern === '0' ? 'value' : 'value2'}.${valueIndex}`;
            const projectForm = formArr[0];
            const menuForms = formArr.slice(1);
            return (
                <div className="business-hour-compound-expand">
                    <div className="business-hour-compound-expand__head">
                        <Typography className="business-hour-compound-expand__title">
                            {i18n.t('business_hour_compound_menu_settings')}
                        </Typography>
                        <IconButton
                            size="small"
                            className="business-hour-compound-expand__collapse-btn"
                            aria-label="collapse"
                            onClick={() => this.setState({ expandId: '' })}
                        >
                            <TablerChevronDownIcon
                                className="business-hour-compound-slot__chevron business-hour-compound-slot__chevron--expanded"
                                aria-hidden
                            />
                        </IconButton>
                    </div>
                    <div className="business-hour-compound-expand__inputs-group">
                        {item.type === '1' && (
                            <Grid container spacing={2} className="business-hour-compound-expand__inputs-row business-hour-compound-expand__inputs-row--date">
                                <Grid item xs={12} md={4} className="basic-input">
                                    <FormFieldCell label={i18n.t('business_hour_page_date')}>
                                        <CommonInput
                                            inputType="DATE"
                                            hiddenLabel
                                            fullWidth
                                            size="small"
                                            handleChangePicker={(v) => {
                                                const newDate = moment(v).format('YYYY-MM-DD');
                                                this.onChange(newDate, `${dayIndex}.date`);
                                            }}
                                            value={item.date}
                                        />
                                    </FormFieldCell>
                                </Grid>
                            </Grid>
                        )}
                        <Grid container spacing={2} className="business-hour-compound-expand__inputs">
                            {inputFormArr.map((formItem) => (
                                <Grid item xs={12} sm={6} md={4} key={formItem.key} className="basic-input">
                                    <FormFieldCell label={formItem.label}>
                                        <CommonInput
                                            inputType="INPUT"
                                            hiddenLabel
                                            fullWidth
                                            size="small"
                                            type={formItem.inputType === 'number' ? 'number' : 'text'}
                                            value={_.get(valueItem, formItem.key, 0)}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                if (formItem.inputType === 'number') {
                                                    const v = Math.floor(Number(event.currentTarget.value));
                                                    if (v >= 0) {
                                                        this.onFormChange(v as unknown as string, key, formItem.key);
                                                    }
                                                } else {
                                                    this.onFormChange(event.currentTarget.value, key, formItem.key);
                                                }
                                            }}
                                        />
                                    </FormFieldCell>
                                </Grid>
                            ))}
                            <Grid item xs={12} className="business-hour-compound-expand__project-row">
                                <div className="business-hour-compound-expand__section business-hour-compound-expand__section--project">
                                    <Typography className="business-hour-compound-expand__section-title">
                                        {i18n.t('business_hour_compound_project_label')}
                                    </Typography>
                                    <div className="business-hour-compound-expand__chips">
                                        {projectForm.options.map((optionItem) => {
                                            const projectList = (_.get(valueItem, 'project', []) as string[]) || [];
                                            const active = projectList.includes(optionItem.value);
                                            return (
                                                <Chip
                                                    key={optionItem.value}
                                                    label={optionItem.label}
                                                    onClick={() => this.onFormChange(optionItem.value, key, 'project')}
                                                    className={`business-hour-compound-project-chip${active ? ' is-active' : ''}`}
                                                    variant="outlined"
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                    </div>
                    {menuForms.map((fm) => renderMenuSection(fm, key, valueItem, item, dayIndex, valueIndex))}
                </div>
            );
        };

        return (
            <div className='business-hour-compound'>
                <div className="business-hour-compound-header business-hour-compound-thead">
                    <div className="business-hour-compound-thead__cell business-hour-compound-thead__cell--title">
                        <Typography variant="h3" component="span" className="title_text">
                            {sectionTitle}
                        </Typography>
                    </div>
                    <div className="business-hour-compound-thead__cell business-hour-compound-thead__cell--24">{i18n.t('business_hour_page_opening_time_24_hours')}</div>
                    <div className="business-hour-compound-thead__cell business-hour-compound-thead__cell--slots-head">
                        <span className="business-hour-compound-thead__daily-label">{i18n.t('business_hour_compound_daily_hours_column')}</span>
                        <div className="business-hour-compound-header__actions">
                            {showAddHolidayButton && (
                                <ButtonComponent
                                    space={false}
                                    titleText={i18n.t('business_hour_compound_add_holiday_opening_time')}
                                    action='ADD'
                                    handleClick={this.addHolidayBusinessHour2}
                                />
                            )}
                            <ButtonComponent
                                space={false}
                                titleText={i18n.t('business_hour_page_add_date_opening_time')}
                                action='ADD'
                                handleClick={this.addBusinessHour2}
                            />
                            <ButtonComponent
                                space={false}
                                action='SAVE'
                                handleClick={this.onSave}
                            />
                            {complexModeToggle}
                        </div>
                    </div>
                    <div className="business-hour-compound-thead__cell business-hour-compound-thead__cell--copy-placeholder" aria-hidden />
                </div>
                <div>
                    {
                        businessHour2.map((item, index) => {
                            const value = (item.pattern === '0' ? item?.value : item?.value2) || [];
                            return (
                                <div
                                    key={item.id}
                                    id={item.id ? `business-hour-compound-day-${item.id}` : undefined}
                                    className="business-hour-compound-day-block"
                                    tabIndex={-1}
                                >
                                    <div className="business-hour-compound-row">
                                        <div className="business-hour-compound-row__day">
                                            {item.type === '0' ? getDate(Number(item.date)) : item.date}
                                        </div>
                                        <div className="business-hour-compound-row__24">
                                            <IOSSwitch
                                                checked={item.pattern === '0'}
                                                onChange={() => this.onChange(item.pattern === '0' ? '1' : '0', `${index}.pattern`)}
                                            />
                                        </div>
                                        <div className="business-hour-compound-row__slots">
                                            {item.pattern === '0'
                                                ? value.map((valueItem, valueIndex) => {
                                                    const isExpand = expandId === `${valueItem.id!}#${item.id}`;
                                                    return (
                                                        <div
                                                            key={valueItem.id}
                                                            className={`business-hour-compound-slot-wrap${isExpand ? ' business-hour-compound-slot-wrap--expanded' : ''}`}
                                                        >
                                                            <div className="business-hour-compound-slot--24h">
                                                                <Typography component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
                                                                    {i18n.t('business_hour_page_opening_time_24_hours')}
                                                                </Typography>
                                                                {this.renderSlotExpandToggle(item, valueItem, isExpand)}
                                                            </div>
                                                            {isExpand && (
                                                                <div className="business-hour-compound-expand-outer">
                                                                    {renderExpandPanel(index, item, valueItem, valueIndex)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                                : value.map((valueItem, valueIndex) => {
                                                    const isExpand = expandId === `${valueItem.id!}#${item.id}`;
                                                    let startTime: Date | null = null;
                                                    let endTime: Date | null = null;
                                                    startTime = moment(valueItem.start_time, 'kk:mm').toDate();
                                                    endTime = moment(valueItem.end_time, 'kk:mm').toDate();
                                                    const key = `${index}.value2.${valueIndex}`;
                                                    /** 仅周一～周日（date 0–6）剩 1 条不可删；节假日（date=7）、自定义日期（type=1）可删最后一条 */
                                                    const canDeleteTimeSlot =
                                                        !isCompoundWeekdayMonToSunRow(item) || value.length > 1;
                                                    return (
                                                        <div
                                                            key={valueItem.id}
                                                            className={`business-hour-compound-slot-wrap${isExpand ? ' business-hour-compound-slot-wrap--expanded' : ''}`}
                                                        >
                                                            <div className="business-hour-compound-slot">
                                                                <IOSSwitch
                                                                    className="business-hour-compound-slot__switch"
                                                                    checked={valueItem.value === '0'}
                                                                    onChange={() => {
                                                                        this.onChange(valueItem.value === '0' ? '1' : '0', `${key}.value`);
                                                                    }}
                                                                />
                                                                <div className="business-hour-compound-slot__time-inner">
                                                                    <CommonInput
                                                                        inputType="TIME_RANGE_COMPACT"
                                                                        rangeHandleChangePicker={(dates) => {
                                                                            if (dates) {
                                                                                this.handleDateChange(
                                                                                    { startTime: dates[0], endTime: dates[1] },
                                                                                    key,
                                                                                );
                                                                            }
                                                                        }}
                                                                        rangeValue={[startTime, endTime]}
                                                                        size="small"
                                                                    />
                                                                </div>
                                                                {this.renderSlotExpandToggle(item, valueItem, isExpand)}
                                                                {canDeleteTimeSlot && (
                                                                    <IconButton
                                                                        size="small"
                                                                        aria-label="delete"
                                                                        onClick={() => this.deleteBusinessHourValue(index, valueIndex)}
                                                                        sx={{
                                                                            padding: 0,
                                                                        }}
                                                                    >
                                                                        <MingcuteCloseFillIcon
                                                                            className="business-hour-compound-slot__delete-icon"
                                                                            aria-hidden
                                                                        />
                                                                    </IconButton>
                                                                )}
                                                            </div>
                                                            {isExpand && (
                                                                <div className="business-hour-compound-expand-outer">
                                                                    {renderExpandPanel(index, item, valueItem, valueIndex)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            {item.pattern === '1' && (
                                                <button
                                                    type="button"
                                                    className="business-hour-compound-add-slot"
                                                    onClick={() => this.addBusinessHourValue(index)}
                                                >
                                                    <IcRoundPlusIcon
                                                        className="business-hour-compound-add-slot__icon"
                                                        aria-hidden
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <div className="business-hour-compound-row__copy">
                                            <IconButton
                                                size="small"
                                                onClick={() => this.setState({ isShowConfirmDialog: true, copySourceIndex: index })}
                                                aria-label={i18n.t('business_hour_page_click_copy')}
                                                sx={{ padding: 0 }}
                                            >
                                                <BusinessHourCopyStackIcon
                                                    className="business-hour-compound-row__copy-icon"
                                                    aria-hidden
                                                />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <Typography
                                        id={`${item.id}`}
                                        className={`form-error ${errorId === item.id ? 'active' : ''}`}
                                    >
                                        {i18n.t('business_hour_page_time_phase_overlap')}
                                    </Typography>
                                </div>
                            );
                        })
                    }
                </div>
                <Dialog
                    open={!!menuModal}
                    onClose={this.closeMenuModal}
                    className="business-hour-compound-menu-dialog"
                    maxWidth={false}
                    PaperProps={{ elevation: 0 }}
                >
                    <DialogContent sx={{ p: 0 }}>
                        {menuModal && (
                            <>
                                <div className="business-hour-compound-menu-dialog__header">
                                    <div>
                                        <div className="business-hour-compound-menu-dialog__title">
                                            {i18n.t('business_hour_compound_add_menu_title')}
                                        </div>
                                        <div className="business-hour-compound-menu-dialog__subtitle">{menuModal.dayTimeLabel}</div>
                                    </div>
                                    <IconButton size="small" onClick={this.closeMenuModal} aria-label="close">
                                        <MingcuteCloseFillIcon
                                            className="business-hour-compound-menu-chip__close"
                                            aria-hidden
                                            focusable="false"
                                        />
                                    </IconButton>
                                </div>
                                <div className="business-hour-compound-menu-dialog__grid">
                                    {menuOptions.map((optionItem) => {
                                        const path = `${menuModal.index}.${menuModal.patternKey}.${menuModal.valueIndex}`;
                                        const vi = _.get(this.props.businessHour2, path) as BusinessHourValue;
                                        const selectedIds = (_.get(vi, menuModal.fieldKey, []) as string[]) || [];
                                        const active = selectedIds.indexOf(optionItem.value) > -1;
                                        return (
                                            <button
                                                type="button"
                                                key={optionItem.value}
                                                className={
                                                    active
                                                        ? 'business-hour-compound-menu-dialog__menu-item business-hour-compound-menu-dialog__menu-item--on'
                                                        : 'business-hour-compound-menu-dialog__menu-item business-hour-compound-menu-dialog__menu-item--off'
                                                }
                                                disabled={
                                                    !active &&
                                                    this.getBtnDisable(menuModal.fieldKey, vi, optionItem.value)
                                                }
                                                onClick={() => {
                                                    this.onFormChange(optionItem.value, path, menuModal.fieldKey);
                                                }}
                                            >
                                                {optionItem.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
                <AlertDialog
                    open={isShowConfirmDialog}
                    size='xs'
                    title={i18n.t('business_hour_page_click_copy')}
                    desc={i18n.t('business_hour_page_this_operate_will_cover_others_if_continue')}
                    onCancel={() => {
                        this.setState({ isShowConfirmDialog: false, copySourceIndex: null });
                    }}
                    onConfirm={this.applyCopyFromRow}
                />
            </div >

        )
    }
}

// *********************
// Type
// *********************
type Props = {
    // 菜单列表
    menuList: FirstMenu[];
    // 复合型营业时间数据
    businessHour2: BusinessHour2[];
    // 修改复合型营业时间数据
    handleChange: (value: BusinessHour2[]) => void;
    // 保存营业时间
    onSave: (value: PostBusinessHour2Params[]) => void;
    // 每人每轮点餐数量默认值
    defaultTurnDishes: number;
    // 轮次时间默认值
    defaultTurnMins: number;
    // 最大轮次默认值
    defaultMaxRound: number;
    /** 与营业时间页 Card 标题一致：营业时间 / 复合型营业时间 */
    sectionTitle: string;
    /** 复合型开关（与页头原 action 一致） */
    complexModeToggle: React.ReactNode;

}
/** 「添加菜单」弹窗状态 */
type MenuModalState = {
    index: number;
    patternKey: 'value' | 'value2';
    valueIndex: number;
    fieldKey: string;
    dayTimeLabel: string;
};

type State = {
    // 当前展开的项
    expandId: string;
    // 表单校验
    errorId: string;
    // 二次确认弹窗
    isShowConfirmDialog: boolean;
    /** 确认后从该行复制到其它周期 */
    copySourceIndex: number | null;
    /** 新增周期后滚动到对应 DOM */
    pendingScrollToDayId: string | null;
    menuModal: MenuModalState | null;
}